import { API_BASE } from '../config';
import { getAccessToken } from '../core/auth/tokenUtil';

export interface WorkspaceMeta {
    id: string;
    name: string;
    is_monitor_tab: boolean;
    version: number;
    created_at: string;
    updated_at: string;
}

export interface WorkspaceFull extends WorkspaceMeta {
    owner_id: string;
    owner_email: string;
    state_json: Record<string, any>;
}

export interface ConflictPayload {
    conflict: true;
    latest_version: number;
    latest_updated_at: string;
    latest_state_json: Record<string, any>;
}

export interface WorkspaceUpdateSuccess {
    success: true;
    version: number;
    updated_at?: string | null;
    unchanged?: boolean;
    stale_match?: boolean;
}



/**
 * Workspace API Service
 * Sends:
 *   - Authorization: Bearer <token>  (verified server-side via JWT)
 *   - Identity derived server-side from validated JWT claims
 */
export class WorkspacesApiService {
    private static base = `${API_BASE}/api/workspaces`;

    private static toUrl(path: string = ''): URL {
        const fullPath = `${this.base}${path}`;
        const isAbsolute = /^https?:\/\//i.test(fullPath);
        return isAbsolute ? new URL(fullPath) : new URL(fullPath, window.location.origin);
    }

    private static async authHeaders(): Promise<HeadersInit> {
        const token = await getAccessToken();
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        };
    }

    static async list(_ownerId: string, _ownerEmail: string, domainId?: string): Promise<WorkspaceFull[]> {
        const url = this.toUrl();
        if (domainId) url.searchParams.append('domain_id', domainId);

        const res = await fetch(url.toString(), {
            headers: await this.authHeaders(),
        });
        if (!res.ok) throw new Error(`Failed to list workspaces: ${res.status}`);
        const json = await res.json();
        return json.data as WorkspaceFull[];
    }

    static async get(_ownerId: string, _ownerEmail: string, id: string, domainId?: string): Promise<WorkspaceFull> {
        const url = this.toUrl(`/${id}`);
        if (domainId) url.searchParams.append('domain_id', domainId);

        const res = await fetch(url.toString(), {
            headers: await this.authHeaders(),
        });
        if (!res.ok) throw new Error(`Failed to get workspace ${id}: ${res.status}`);
        const json = await res.json();
        return json.data as WorkspaceFull;
    }

    static async create(
        _ownerId: string,
        _ownerEmail: string,
        id: string,
        name: string,
        stateJson: Record<string, any>,
        domainId?: string
    ): Promise<{ id: string }> {
        const payload: Record<string, any> = { id, name, state_json: stateJson };
        if (domainId) payload.domain_id = domainId;

        const res = await fetch(this.base, {
            method: 'POST',
            headers: await this.authHeaders(),
            body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`Create workspace failed: ${res.status}`);
        return res.json();
    }

    /**
     * Update with optimistic concurrency. On a 409 conflict, returns the conflict payload
     * instead of throwing so the caller can show a merge/reload dialog.
     */
    static async update(
        _ownerId: string,
        _ownerEmail: string,
        id: string,
        payload: { name?: string; state_json?: Record<string, any> },
        version: number,
        domainId?: string
    ): Promise<WorkspaceUpdateSuccess | ConflictPayload> {
        const url = this.toUrl(`/${id}`);
        if (domainId) url.searchParams.append('domain_id', domainId);

        const res = await fetch(url.toString(), {
            method: 'PUT',
            headers: {
                ...(await this.authHeaders()),
                'If-Match': `"${version}"`,
            },
            body: JSON.stringify(payload),
        });

        if (res.status === 409) {
            const json = await res.json();
            return json as ConflictPayload;
        }
        if (!res.ok) throw new Error(`Update workspace failed: ${res.status}`);
        return await res.json();
    }

    static async forceUpdate(
        _ownerId: string,
        _ownerEmail: string,
        id: string,
        payload: { name?: string; state_json?: Record<string, any> },
        domainId?: string
    ): Promise<WorkspaceUpdateSuccess> {
        const url = this.toUrl(`/${id}`);
        url.searchParams.append('force', 'true');
        if (domainId) url.searchParams.append('domain_id', domainId);

        const res = await fetch(url.toString(), {
            method: 'PUT',
            headers: await this.authHeaders(),
            body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`Force update workspace failed: ${res.status}`);
        return await res.json();
    }

    static async remove(_ownerId: string, _ownerEmail: string, id: string, domainId?: string): Promise<void> {
        const url = this.toUrl(`/${id}`);
        if (domainId) url.searchParams.append('domain_id', domainId);

        const res = await fetch(url.toString(), {
            method: 'DELETE',
            headers: await this.authHeaders(),
        });
        if (res.status === 403) throw new Error('Cannot delete system Monitor tab.');
        if (!res.ok) throw new Error(`Delete workspace failed: ${res.status}`);
    }
}
